import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Country, Property } from '@app/shared/interfaces';
import { LegalEntity } from '@app/shared/interfaces/legal-entity.interface';
import { CountriesService } from '@app/shared/services/countries.service';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { Observable, forkJoin, interval } from 'rxjs';
import { defaultIfEmpty, finalize, ignoreElements, takeWhile, tap } from 'rxjs/operators';

@Component({
  selector: 'sbnb-create-booking-listing-dialog',
  templateUrl: './create-booking-listing-dialog.component.html',
  styleUrls: ['./create-booking-listing-dialog.component.scss'],
})
export class CreateBookingListingDialogComponent implements OnInit {
  countries$: Observable<Country[]>;
  legalEntities: Array<LegalEntity> = [];
  loading = false;
  loadingEntities = false;
  legalEntityForm: UntypedFormGroup;
  defaultEntity: LegalEntity | null = null;
  propertyId: number;
  propertyLoading = true;
  property$: Observable<Property>;
  stage:
    | 'has_account'
    | 'existing_account'
    | 'create_account_email'
    | 'confirm_existing_email'
    | 'enter_legal_entity_id_and_email'
    | 'entity_type'
    | 'entity_details'
    | 'ready_to_sync'
    | 'syncing'
    | 'sync_complete' = 'has_account';
  entityType: 'individual' | 'organization' | null = null;
  newAccountEmailForm: UntypedFormGroup;
  emailCheckForm: UntypedFormGroup;
  selectedEntity: LegalEntity | null = null;
  potentialExistingEntity: LegalEntity | null = null;
  newEntityEmail: string | null = null;
  syncErrors: { message: string; errors: string[] } | null = null;
  emailError: string | null = null;
  hasExistingAccount: boolean | null = null;
  progressMessages: string[] = [];
  showProgressMessage = false;
  currentProgressIndex = 0;
  progressMessagesList = [
    'Creating the listing record on Booking.com...',
    'Adding basic details such as listing title, property category, full address...',
    'Adding public host profile...',
    'Configuring booking model to be request-to-book...',
    'Configuring amenities...',
    'Adding taxes & fees...',
    'Creating the rate plan and cancellation policy...',
    'Configuring bedrooms, bathrooms, and their details...',
    'Pushing photos...',
    'Finalizing...',
  ];
  legalEntityIdAndEmailForm: UntypedFormGroup;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private propertiesService: PropertiesService,
    private countriesService: CountriesService,
    private fb: UntypedFormBuilder,
    private segmentIoService: SegmentIoService
  ) {
    this.initForm();
    this.initNewAccountEmailForm();
    this.initEmailCheckForm();
    this.initLegalEntityIdAndEmailForm();
  }

  ngOnInit(): void {
    this.getPropertyIdFromRouteParams();
    this.loadLegalEntities();
    this.loadCountries();
    this.segmentIoService.track(SegmentEvent.BdcListingCreateOpened, { property_id: this.propertyId });
  }

  private getPropertyIdFromRouteParams() {
    this.route.params.subscribe((params) => {
      this.propertyId = params['id'];
      this.fetchPropertyDetails();
    });
  }

  private fetchPropertyDetails() {
    this.property$ = this.propertiesService
      .getProperty(this.propertyId)
      .pipe(tap(() => (this.propertyLoading = false)));
  }

  private initForm(): void {
    this.legalEntityForm = this.fb.group({
      legal_name: [''],
      company_name: ['', [Validators.required]],
      legal_contact_name: ['', [Validators.required]],
      legal_contact_phone_number: [null],
      country: [''],
    });
  }

  private initNewAccountEmailForm(): void {
    this.newAccountEmailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  private initEmailCheckForm(): void {
    this.emailCheckForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  private initLegalEntityIdAndEmailForm(): void {
    this.legalEntityIdAndEmailForm = this.fb.group({
      legalEntityId: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
      email: ['', [Validators.required, Validators.email]],
    });
  }

  private loadLegalEntities(): void {
    this.loadingEntities = true;
    this.propertiesService.getLegalEntities(this.propertyId).subscribe({
      next: (response) => {
        this.legalEntities = response.data.filter((entity) => entity.exists_in_bdc);
        this.defaultEntity = response.data.find((entity) => entity.is_default);
      },
      complete: () => {
        this.loadingEntities = false;
      },
    });
  }

  private loadCountries(): void {
    this.countries$ = this.countriesService.getCountries();
  }

  onHasAccountSelected(hasAccount: boolean): void {
    this.hasExistingAccount = hasAccount;
    if (hasAccount) {
      this.stage = 'existing_account';
      this.segmentIoService.track(SegmentEvent.BdcListingCreateSelectedExisting, { property_id: this.propertyId });
    } else {
      this.stage = 'create_account_email';
      this.segmentIoService.track(SegmentEvent.BdcListingCreateSelectedNew, { property_id: this.propertyId });
    }
  }

  onExistingAccountSelected(entity: LegalEntity): void {
    this.selectedEntity = entity;
    this.stage = 'ready_to_sync';
    this.segmentIoService.track(SegmentEvent.BdcListingCreateAccountSelected, {
      property_id: this.propertyId,
      legal_entity_id: entity.legal_entity_id,
    });
  }

  onSubmitEmail(): void {
    if (this.newAccountEmailForm.valid) {
      this.loading = true;
      this.emailError = null;
      const email = this.newAccountEmailForm.get('email').value;
      this.segmentIoService.track(SegmentEvent.BdcListingCreateNewEmailSubmitted, {
        property_id: this.propertyId,
        email,
      });

      this.propertiesService.checkLegalEntityExists(this.propertyId, email).subscribe({
        next: (entity: LegalEntity | null) => {
          this.loading = false;
          if (entity) {
            this.potentialExistingEntity = entity;
            this.stage = 'confirm_existing_email';
            this.emailError = null;
          } else {
            this.newEntityEmail = email;
            this.stage = 'entity_type';
            this.newAccountEmailForm.reset();
          }
        },
        error: (error) => {
          this.loading = false;
          this.emailError = error?.error?.message || 'An error occurred while checking the email address.';
        },
      });
    }
  }

  onEntityTypeSelected(): void {
    this.stage = 'entity_details';
    if (this.defaultEntity) {
      const formValue = {
        ...this.defaultEntity,
        country: this.defaultEntity.country?.toUpperCase(),
      };

      if (this.entityType === 'individual') {
        formValue.company_name = formValue.legal_contact_name;
        formValue.legal_name = '';
      }

      this.legalEntityForm.patchValue(formValue);
    }
  }

  onSubmitCreateLegalEntity(): void {
    if (this.legalEntityForm.valid) {
      this.loading = true;

      const formValue = {
        ...this.legalEntityForm.value,
        legal_contact_email: this.newEntityEmail,
      };

      if (this.entityType === 'individual') {
        formValue.company_name = formValue.legal_contact_name;
        formValue.legal_name = '';
      }

      this.propertiesService.createLegalEntity(this.propertyId, formValue).subscribe({
        next: (response) => {
          this.selectedEntity = response.data as unknown as LegalEntity;
          this.stage = 'ready_to_sync';
        },
        complete: () => {
          this.loading = false;
        },
      });
    }
  }

  onCancel(): void {
    this.segmentIoService.track(SegmentEvent.BdcListingCreateFlowCancelled, {
      property_id: this.propertyId,
      stage: this.stage,
    });
    this.router.navigate([`/properties/property/${this.propertyId}`]);
  }

  onBack(): void {
    const previousStage = this.stage;
    if (this.stage === 'entity_details') {
      this.stage = 'entity_type';
    } else if (this.stage === 'entity_type') {
      this.stage = 'create_account_email';
      this.emailError = null;
    } else if (this.stage === 'existing_account') {
      this.stage = 'has_account';
      this.hasExistingAccount = null;
      this.emailCheckForm.reset();
      this.emailError = null;
    } else if (this.stage === 'confirm_existing_email') {
      this.stage = 'create_account_email';
      this.potentialExistingEntity = null;
    } else if (this.stage === 'create_account_email') {
      this.stage = 'has_account';
      this.hasExistingAccount = null;
      this.newAccountEmailForm.reset();
      this.emailError = null;
    } else if (this.stage === 'ready_to_sync') {
      if (this.legalEntityIdAndEmailForm.value.legalEntityId) {
        this.stage = 'enter_legal_entity_id_and_email';
      } else if (this.hasExistingAccount) {
        this.stage = 'existing_account';
      } else {
        this.stage = 'entity_details';
      }
    } else if (this.stage === 'enter_legal_entity_id_and_email') {
      this.stage = 'has_account';
      this.hasExistingAccount = null;
      this.legalEntityIdAndEmailForm.reset();
      this.emailError = null;
    }
    this.segmentIoService.track(SegmentEvent.BdcListingCreateBackPressed, {
      property_id: this.propertyId,
      from_stage: previousStage,
      to_stage: this.stage,
    });
  }

  onStartSync(): void {
    this.stage = 'syncing';
    this.syncErrors = null;
    this.progressMessages = [this.progressMessagesList[0]];
    this.currentProgressIndex = 1;
    this.showProgressMessage = true;

    this.segmentIoService.track(SegmentEvent.BdcListingCreateSyncStarted, {
      property_id: this.propertyId,
      email: this.selectedEntity?.login_email,
      legal_entity_id: this.selectedEntity?.legal_entity_id,
    });

    const progress$: Observable<void> = this.startProgressMessages();

    const create$: Observable<unknown> = this.propertiesService.createBookingProperty(
      this.propertyId,
      this.selectedEntity.login_email,
      this.selectedEntity?.legal_entity_id?.toString()
    );

    forkJoin([progress$, create$])
      .pipe(
        finalize(() => {
          this.showProgressMessage = false;
        })
      )
      .subscribe({
        next: () => {
          this.stage = 'sync_complete';
          this.syncErrors = null;
          this.segmentIoService.track(SegmentEvent.BdcListingCreateSyncSucceeded, {
            property_id: this.propertyId,
            email: this.selectedEntity?.login_email,
            legal_entity_id: this.selectedEntity?.legal_entity_id,
          });
        },
        error: (error) => {
          this.syncErrors = {
            message: error?.error?.message || 'Failed to create listing on Booking.com',
            errors:
              error.error?.errors?.length > 0
                ? error.error.errors
                : [error?.error?.message || 'An unexpected error occurred during the sync process. Please try again.'],
          };

          this.segmentIoService.track(SegmentEvent.BdcListingCreateSyncFailed, {
            property_id: this.propertyId,
            email: this.selectedEntity?.login_email,
            legal_entity_id: this.selectedEntity?.legal_entity_id,
            error_message: this.syncErrors.message,
            error_details: this.syncErrors.errors.join('; '),
          });
        },
      });
  }

  private startProgressMessages(): Observable<void> {
    return interval(800 + Math.random() * 2000).pipe(
      tap(() => {
        if (this.currentProgressIndex < this.progressMessagesList.length && this.showProgressMessage) {
          this.progressMessages.push(this.progressMessagesList[this.currentProgressIndex]);
          this.currentProgressIndex++;
        }
      }),
      takeWhile(() => this.currentProgressIndex < this.progressMessagesList.length && this.showProgressMessage, true),
      ignoreElements(),
      defaultIfEmpty(undefined)
    );
  }

  onFinished(): void {
    this.segmentIoService.track(SegmentEvent.BdcListingCreateFlowCompleted, { property_id: this.propertyId });
    this.router.navigate([`/properties/property/${this.propertyId}`]);
  }

  checkEmail(): void {
    if (this.emailCheckForm.valid) {
      this.loading = true;
      this.emailError = null;
      const email = this.emailCheckForm.get('email').value;
      this.segmentIoService.track(SegmentEvent.BdcListingCreateExistingEmailSubmitted, {
        property_id: this.propertyId,
        email,
      });

      this.propertiesService.checkLegalEntityExists(this.propertyId, email).subscribe({
        next: (entity: LegalEntity | null) => {
          this.loading = false;
          if (entity) {
            this.selectedEntity = entity;
            this.stage = 'ready_to_sync';
            this.emailCheckForm.reset();
          } else {
            this.emailError =
              'No Booking.com account found with this email. Please check the email or go back to create a new account.';
          }
        },
        error: (error) => {
          this.loading = false;
          this.emailError = error?.error?.message || 'An error occurred while checking the email address.';
        },
      });
    }
  }

  onConfirmExistingAccount(): void {
    if (this.potentialExistingEntity) {
      this.segmentIoService.track(SegmentEvent.BdcListingCreateExistingEmailConfirmed, {
        property_id: this.propertyId,
        email: this.potentialExistingEntity.login_email,
        legal_entity_id: this.potentialExistingEntity.legal_entity_id,
      });
      this.selectedEntity = this.potentialExistingEntity;
      this.hasExistingAccount = true;
      this.stage = 'ready_to_sync';
      this.potentialExistingEntity = null;
      this.newAccountEmailForm.reset();
      this.emailCheckForm.reset();
      this.emailError = null;
    } else if (this.stage === 'enter_legal_entity_id_and_email') {
      this.stage = 'has_account';
      this.hasExistingAccount = null;
      this.legalEntityIdAndEmailForm.reset();
      this.emailError = null;
    } else if (this.stage === 'confirm_existing_email') {
      this.stage = 'create_account_email';
      this.potentialExistingEntity = null;
    }
  }

  onRejectExistingAccount(): void {
    this.segmentIoService.track(SegmentEvent.BdcListingCreateExistingEmailRejected, {
      property_id: this.propertyId,
      email: this.potentialExistingEntity?.login_email,
    });
    this.stage = 'create_account_email';
    this.potentialExistingEntity = null;
    this.newAccountEmailForm.reset();
  }

  onContinueWithLegalEntityId(): void {
    this.stage = 'enter_legal_entity_id_and_email';
    this.segmentIoService.track(SegmentEvent.BdcListingCreateSelectedLegalEntityId, { property_id: this.propertyId });
  }

  onSubmitLegalEntityIdAndEmail(): void {
    if (this.legalEntityIdAndEmailForm.valid) {
      const formValue = this.legalEntityIdAndEmailForm.value;
      const providedId = formValue.legalEntityId;
      const providedEmail = formValue.email;

      this.segmentIoService.track(SegmentEvent.BdcListingCreateLegalEntityIdSubmitted, {
        property_id: this.propertyId,
        email: providedEmail,
        legal_entity_id: providedId,
      });

      this.selectedEntity = {
        legal_entity_id: providedId,
        login_email: providedEmail,
      } as unknown as LegalEntity;

      this.stage = 'ready_to_sync';
    }
  }
}
